// The two writes the funnel makes — the state half of the wizard, split from the
// route the way `createRepoToggle` is.
//
// Both used to fail silently, and both failures landed on the one screen a new
// account cannot skip:
//
//  - every `advance` rejection went into one bare `catch`. The comment justified
//    swallowing a 409 (the funnel raced under us — refetching IS the answer); the
//    code swallowed 500s, 403s and dropped connections with it, and the route
//    carried no `createToast` at all.
//  - copying the setup prompt advanced the funnel whether or not the clipboard
//    took it. `yml_requested` has no backward edge, so a non-secure context moved
//    the user past the only screen showing the text they were told to copy.

import type { OnboardingEvent, OnboardingState } from '@developerz/domain';
import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { ApiError, errorReason } from './api';
import { copyText } from './clipboard';
import { advanceOnboarding, type IntendedTier } from './onboarding';
import { ONBOARDING_STEPS } from './onboardingSteps';
import type { ToastTone } from './toast';

export type OnboardingActions = {
  /** True while a write is in flight — drives the cards' `aria-disabled` guard. */
  readonly busy: () => boolean;
  readonly advance: (event: OnboardingEvent, intendedTier?: IntendedTier) => void;
  /** Copy `text`; on success, fire the CURRENT step's advance if it has one. */
  readonly copy: (text: string) => void;
};

export function createOnboardingActions(input: {
  readonly token: string | null;
  /** The current funnel state, read reactively — never snapshotted. */
  readonly state: () => OnboardingState | undefined;
  readonly refetch: () => Promise<unknown>;
  readonly notify: (message: string, tone: ToastTone) => void;
}): OnboardingActions {
  const [busy, setBusy] = createSignal(false);

  const run = async (event: OnboardingEvent, intendedTier?: IntendedTier): Promise<void> => {
    setBusy(true);
    try {
      await advanceOnboarding(event, input.token, intendedTier);
    } catch (error) {
      // 409 ONLY is silent: the funnel moved under us (a webhook, or a second
      // tab), and re-rendering from the server's truth is both the fix and the
      // whole message. Anything else is a refusal the user has to be told about.
      if (!(error instanceof ApiError && error.status === 409)) {
        input.notify(errorReason(error, t('onboarding.advance_failed')), 'error');
      }
    } finally {
      await input.refetch();
      setBusy(false);
    }
  };

  const copy = async (text: string): Promise<void> => {
    if (!(await copyText(text))) {
      input.notify(t('onboarding.yml_copy_failed'), 'error');
      return;
    }
    input.notify(t('onboarding.yml_copied'), 'success');
    const state = input.state();
    const next = state === undefined ? null : ONBOARDING_STEPS[state].advanceEvent;
    if (next !== null) await run(next);
  };

  return {
    busy,
    advance: (event, intendedTier) => void run(event, intendedTier),
    copy: (text) => void copy(text),
  };
}
